import { useAuthReady } from './useAuthReady';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { fetchBtcFeeRate, fetchBtcUtxos } from '../lib/btc';

/**
 * Exact BTC Max + fee via the same selectUTXO path as the real send.
 * Prefer the real recipient for output sizing; fall back to the change
 * address (P2WPKH) when recipient is unknown yet (e.g. swap Max).
 */
export function useBtcSpendQuote(
  address: string | undefined,
  publicKeyHex: string | undefined,
  amountBtc?: number,
  recipient?: string,
) {
  const { canFetch, initData } = useAuthReady();
  const ready = canFetch && !!address && !!publicKeyHex;
  const amountKey =
    amountBtc != null && amountBtc > 0 ? Math.round(amountBtc * 1e8) : 0;
  const toAddr = recipient && recipient.length > 10 ? recipient : address;

  const q = useQuery({
    queryKey: ['btcSpendQuote', address, publicKeyHex, amountKey, toAddr ?? ''],
    enabled: ready,
    queryFn: async () => {
      const [{ planBtcMaxSend, planBtcTx }, utxos, feeRate] = await Promise.all([
        import('@incpt/blockchain/btc'),
        fetchBtcUtxos(initData!, address!),
        fetchBtcFeeRate(initData!),
      ]);
      const confirmed = utxos
        .filter((u) => u.status.confirmed)
        .map((u) => ({ txid: u.txid, vout: u.vout, valueSat: BigInt(u.value) }));
      const base = {
        utxos: confirmed,
        to: toAddr!,
        feeRateSatVb: feeRate,
        changeAddress: address!,
        publicKeyHex: publicKeyHex!,
      };
      const { maxSat, feeSat: maxFeeSat } = planBtcMaxSend(base);
      let feeSat = maxFeeSat;
      if (amountKey > 0 && BigInt(amountKey) <= maxSat) {
        try {
          feeSat = planBtcTx({ ...base, amountSat: BigInt(amountKey) }).feeSat;
        } catch {
          /* keep max fee */
        }
      }
      return {
        maxBtc: Number(maxSat) / 1e8,
        feeBtc: Number(feeSat) / 1e8,
        feeRate,
      };
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });

  return {
    maxBtc: q.data?.maxBtc,
    feeBtc: q.data?.feeBtc,
    ready: q.data != null && !q.isPlaceholderData,
    loading: q.isLoading,
  };
}
